import React from "react";
import {
  Grid,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import { buildInvoicePreview } from "../company/company.constants";

const TeamBillingInfoSection = ({
  values,
  touched,
  errors,
  getFieldProps,
  setFieldValue,
  isReadOnly,
  company,
}) => {
  // Invoice format comes from the parent company. Prefer the copy returned on the
  // team payload (billingInfo.invoiceNumbering) so the preview is exact without a
  // second call; fall back to the company prop's taxBillingConfig.
  const invoiceNumbering =
    values.billingInfo?.invoiceNumbering ||
    company?.taxBillingConfig?.invoiceNumbering ||
    {};

  // Teams without a GSTIN bill on PAN alone, so GSTIN is only collected (and
  // only required) when "Have GST" is selected.
  const hasGst = values.billingInfo?.hasGst !== false;

  const companyName = company?.name || "";
  // entity_code is the Tax Entity (GSTIN/PAN owner) sequence code assigned by
  // the backend TaxEntityService (01, 02, 03...). Teams sharing the same GSTIN
  // share the same entity_code and the same invoice sequence. Until the API
  // resolves the team's tax entity we show "XX" so the segment reads as a
  // placeholder rather than a real (and possibly wrong) code.
  const resolvedEntityCode =
    values.billingInfo?.entityCode || values.taxEntity?.entityCode || "";
  const isEntityCodeResolved = Boolean(resolvedEntityCode);
  const preview = buildInvoicePreview(
    invoiceNumbering,
    companyName,
    isEntityCodeResolved ? resolvedEntityCode : "XX"
  );

  return (
    <Grid container spacing={3} className="cms-grid-mb">
      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ pb: 1, fontWeight: 600 }}>
          BILLING IDENTITY
        </Typography>
        <Typography variant="body2" color="text.secondary">
          GSTIN and PAN identify the legal billing entity that owns this team's
          invoice sequence.
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <FormControl disabled={isReadOnly}>
          <RadioGroup
            row
            name="billingInfo.hasGst"
            value={hasGst ? "yes" : "no"}
            onChange={(e) => {
              const nextHasGst = e.target.value === "yes";
              setFieldValue("billingInfo.hasGst", nextHasGst);
              if (!nextHasGst) {
                setFieldValue("billingInfo.gstin", "");
              }
            }}
          >
            <FormControlLabel
              value="yes"
              control={<Radio size="small" />}
              label="Have GST"
            />
            <FormControlLabel
              value="no"
              control={<Radio size="small" />}
              label="No GST"
            />
          </RadioGroup>
        </FormControl>
      </Grid>

      {hasGst && (
        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small">
            <TextField
              required
              id="billing-gstin"
              label="GSTIN"
              size="small"
              placeholder="Ex: 22ABCDE1234F1Z5"
              inputProps={{
                style: { textTransform: "uppercase" },
                maxLength: 15,
              }}
              disabled={isReadOnly}
              {...getFieldProps("billingInfo.gstin")}
              onChange={(e) =>
                setFieldValue("billingInfo.gstin", e.target.value.toUpperCase())
              }
              error={Boolean(
                touched.billingInfo?.gstin && errors.billingInfo?.gstin
              )}
              helperText={
                touched.billingInfo?.gstin && errors.billingInfo?.gstin
              }
            />
          </FormControl>
        </Grid>
      )}

      <Grid item xs={12} md={6}>
        <FormControl fullWidth size="small">
          <TextField
            required
            id="billing-pan"
            label="PAN"
            size="small"
            placeholder="Ex: ABCDE1234F"
            inputProps={{
              style: { textTransform: "uppercase" },
              maxLength: 10,
            }}
            disabled={isReadOnly}
            {...getFieldProps("billingInfo.pan")}
            onChange={(e) =>
              setFieldValue("billingInfo.pan", e.target.value.toUpperCase())
            }
            error={Boolean(touched.billingInfo?.pan && errors.billingInfo?.pan)}
            helperText={touched.billingInfo?.pan && errors.billingInfo?.pan}
          />
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <Box
          sx={{
            p: 2,
            borderRadius: 1,
            bgcolor: "action.hover",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ lineHeight: 1 }}
          >
            Invoice Preview:
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontFamily: "monospace", lineHeight: 1, m: 0 }}
          >
            {preview}
          </Typography>
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.75 }}
        >
          {!isEntityCodeResolved &&
            "Sample — the entity code (XX) is assigned on save. "}
          Format is configured in Company → Invoice Numbering.
        </Typography>
      </Grid>
    </Grid>
  );
};

export default TeamBillingInfoSection;
